import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '../api';
import { emailWordingEndpoints } from '../api-email-wording';
import type { EmailWordingInput, EmailWordingTemplate, EmailWordingVersion, RenderedEmailPreview } from '../api-email-wording';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Field, Panel, Table, formatWhen, statusTone } from '../ui';

/**
 * Email wording (D-357).
 *
 * Staff may change three things about a customer email: the subject line, a
 * paragraph at the top and a paragraph at the end. Everything else — the
 * tickets, the money, the links, a receipt's legal statement — is the code's,
 * and the preview shows the words in place around a made-up order so nobody
 * has to imagine where they land.
 *
 * Shaped like Terms, for the same reason: a published version is frozen, so
 * the draft is the only editable thing, publishing is confirmed, and going
 * back to the built-in words is its own plainly named button rather than
 * "publish something empty".
 */

type FieldName = keyof EmailWordingInput;

const EMPTY = { subject: '', intro: '', closing: '', note: '' };

export default function EmailWordingPage() {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [lastField, setLastField] = useState<FieldName>('subject');
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<RenderedEmailPreview | null>(null);

  const list = useQuery({ queryKey: ['email-wording'], queryFn: emailWordingEndpoints.list });
  const detail = useQuery({ queryKey: ['email-wording', selected], queryFn: () => emailWordingEndpoints.detail(selected!), enabled: selected !== null });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['email-wording'] });
  const onError = (cause: ApiError) => setError(cause.message);

  const template = detail.data?.template ?? null;
  const input = (): EmailWordingInput => ({ subject: form.subject.trim() || null, intro: form.intro.trim() || null, closing: form.closing.trim() || null });

  const previewer = useMutation({
    mutationFn: (wording: EmailWordingInput) => emailWordingEndpoints.preview(selected!, wording),
    onSuccess: (result) => {
      setError(null);
      setPreview(result.email);
    },
    onError,
  });

  /*
   * The editor is filled once per template, from its draft, else from what is
   * published, else empty — never again while somebody is typing, so a refetch
   * cannot overwrite what they have written. The first preview is of exactly
   * that, so opening an email shows it as it goes out now.
   */
  useEffect(() => {
    if (!template) return;
    const source = template.draft ?? template.active;
    const key = `${template.code}:${source?.id ?? 'built-in'}`;
    if (key === loadedKey) return;
    const next = { subject: source?.subject ?? '', intro: source?.intro ?? '', closing: source?.closing ?? '', note: template.draft?.change_note ?? '' };
    setForm(next);
    setLoadedKey(key);
    setPreview(null);
    previewer.mutate({ subject: next.subject || null, intro: next.intro || null, closing: next.closing || null });
    // Keyed on the version being loaded, not on `previewer`, which is a new object every render.
  }, [template, loadedKey]);

  const save = useMutation({
    mutationFn: () => emailWordingEndpoints.saveDraft(selected!, { ...input(), change_note: form.note.trim() || null }),
    onSuccess: (result) => {
      setError(null);
      setLoadedKey(`${result.draft.template_code}:${result.draft.id}`);
      void refresh();
      previewer.mutate(input());
    },
    onError,
  });
  const discard = useMutation({
    mutationFn: () => emailWordingEndpoints.discardDraft(selected!),
    onSuccess: () => {
      setError(null);
      setLoadedKey(null);
      void refresh();
    },
    onError,
  });
  const publish = useMutation({
    mutationFn: () => emailWordingEndpoints.publish(selected!),
    onSuccess: () => {
      setError(null);
      setLoadedKey(null);
      void refresh();
    },
    onError,
  });
  const revert = useMutation({
    mutationFn: () => emailWordingEndpoints.revert(selected!),
    onSuccess: () => {
      setError(null);
      setLoadedKey(null);
      void refresh();
    },
    onError,
  });

  if (!can('settings.manage')) return <NotPermitted permission="settings.manage" />;
  const editable = can('email_wording.manage') && template?.editable === true;
  const templates = list.data?.templates ?? [];
  const busy = save.isPending || publish.isPending || revert.isPending || discard.isPending;

  const insert = (name: string) => setForm((current) => ({ ...current, [lastField]: `${current[lastField]}{{${name}}}` }));

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}

      <Panel
        title="Customer emails"
        description="The subject line and a paragraph at the top and the end of each email can be changed here. Tickets, amounts, links and legal wording always come from the system."
      >
        <Table headers={['Email', 'Wording', '']}>
          {list.isPending ? (
            <EmptyRow columns={3}>Loading…</EmptyRow>
          ) : templates.length === 0 ? (
            <EmptyRow columns={3}>No emails.</EmptyRow>
          ) : (
            templates.map((row) => <TemplateRow key={row.code} template={row} selected={row.code === selected} onOpen={() => setSelected(row.code === selected ? null : row.code)} />)
          )}
        </Table>
      </Panel>

      {selected && template ? (
        <>
          <Panel
            title={template.name}
            description={
              !template.editable
                ? 'This email goes to staff, not customers, so its wording is not editable. The preview shows what it looks like.'
                : template.draft
                  ? `Draft version ${template.draft.version}, saved ${formatWhen(template.draft.updated_at)}. Customers still get ${template.active ? `version ${template.active.version}` : 'the built-in wording'} until it is published.`
                  : template.active
                    ? `Version ${template.active.version} is live, published ${formatWhen(template.active.activated_at)}. Saving starts a draft; nothing changes for customers until it is published.`
                    : 'Customers get the built-in wording. Saving starts a draft; nothing changes for customers until it is published.'
            }
          >
            <div className="space-y-4 px-5 py-4">
              <Field label="Subject line" htmlFor="wording-subject" hint="Leave blank to keep the built-in subject.">
                <input
                  id="wording-subject"
                  value={form.subject}
                  onChange={(event) => setForm({ ...form, subject: event.target.value })}
                  onFocus={() => setLastField('subject')}
                  disabled={!editable}
                  maxLength={200}
                  className="w-full rounded border border-line bg-surface px-3 py-2 text-sm"
                />
              </Field>
              <Field label="Opening paragraph" htmlFor="wording-intro" hint="Shown first, under the heading. Plain text; line breaks are kept. Leave blank for none.">
                <textarea
                  id="wording-intro"
                  value={form.intro}
                  onChange={(event) => setForm({ ...form, intro: event.target.value })}
                  onFocus={() => setLastField('intro')}
                  disabled={!editable}
                  rows={4}
                  maxLength={2000}
                  className="w-full rounded border border-line bg-surface px-3 py-2 text-sm"
                />
              </Field>
              <Field label="Closing paragraph" htmlFor="wording-closing" hint="Shown last, above the footer. Plain text; line breaks are kept. Leave blank for none.">
                <textarea
                  id="wording-closing"
                  value={form.closing}
                  onChange={(event) => setForm({ ...form, closing: event.target.value })}
                  onFocus={() => setLastField('closing')}
                  disabled={!editable}
                  rows={3}
                  maxLength={2000}
                  className="w-full rounded border border-line bg-surface px-3 py-2 text-sm"
                />
              </Field>
              {template.placeholders.length > 0 ? (
                <div>
                  <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Placeholders this email can use</p>
                  <ul className="mt-2 space-y-1">
                    {template.placeholders.map((placeholder) => (
                      <li key={placeholder.name} className="flex flex-wrap items-center gap-2 text-sm text-ink-600">
                        <Button disabled={!editable} onClick={() => insert(placeholder.name)} aria-label={`Insert {{${placeholder.name}}}`}>
                          <code className="text-xs">{`{{${placeholder.name}}}`}</code>
                        </Button>
                        {placeholder.label}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <Field label="What changed (for staff, never shown to customers)" htmlFor="wording-note">
                <input id="wording-note" value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} disabled={!editable} maxLength={500} className="w-full rounded border border-line bg-surface px-3 py-2 text-sm" />
              </Field>
              <div className="flex flex-wrap gap-2">
                <Button disabled={previewer.isPending} onClick={() => previewer.mutate(input())}>
                  {previewer.isPending ? 'Rendering…' : 'Preview'}
                </Button>
                {editable ? (
                  <>
                    <Button disabled={busy} onClick={() => save.mutate()}>
                      {save.isPending ? 'Saving…' : 'Save draft'}
                    </Button>
                    {template.draft ? (
                      <Button
                        variant="primary"
                        disabled={busy}
                        onClick={() => {
                          /*
                             Confirmed by naming the version, because this is the
                             step customers see: from now on every one of these
                             emails says it, and the text can never be edited.
                          */
                          if (window.confirm(`Publish version ${template.draft!.version} of “${template.name}”? Every one of these emails sent from now on uses it, and its text can never be changed afterward. Save first if you have edited since.`)) publish.mutate();
                        }}
                      >
                        Publish version {template.draft.version}
                      </Button>
                    ) : null}
                    {template.draft ? (
                      <Button variant="danger" disabled={busy} onClick={() => window.confirm('Throw this draft away?') && discard.mutate()}>
                        Discard draft
                      </Button>
                    ) : null}
                    {template.active ? (
                      <Button
                        variant="danger"
                        disabled={busy}
                        onClick={() => window.confirm(`Stop using version ${template.active!.version} and go back to the built-in wording for “${template.name}”?`) && revert.mutate()}
                      >
                        Use the built-in wording
                      </Button>
                    ) : null}
                  </>
                ) : template.editable ? (
                  <p className="self-center text-xs text-ink-400">Changing the wording needs email_wording.manage.</p>
                ) : null}
              </div>
            </div>
          </Panel>

          <Panel title="Preview" description="The words in the editor, around a sample order. Saved or not, this is how the email renders.">
            {preview ? <EmailPreview email={preview} /> : <p className="px-5 py-4 text-sm text-ink-500">{previewer.isPending ? 'Rendering…' : 'Press Preview to see the email.'}</p>}
          </Panel>

          <Panel title="Every version" description="Kept forever, because each sent email records the version it used.">
            <Table headers={['Version', 'Status', 'Published', 'Subject', 'What changed']}>
              {(detail.data?.versions ?? []).length === 0 ? (
                <EmptyRow columns={5}>No custom wording yet. Customers get the built-in email.</EmptyRow>
              ) : (
                detail.data!.versions.map((version) => <VersionRow key={version.id} version={version} />)
              )}
            </Table>
          </Panel>
        </>
      ) : null}
    </div>
  );
}

function TemplateRow({ template, selected, onOpen }: { template: EmailWordingTemplate; selected: boolean; onOpen: () => void }) {
  return (
    <tr className="border-b border-line last:border-0">
      <td className="px-5 py-3">
        <p className="text-sm font-medium text-ink-900">{template.name}</p>
        <p className="text-xs text-ink-400">{template.description}</p>
      </td>
      <td className="px-5 py-3 whitespace-nowrap">
        <span className="inline-flex flex-wrap gap-1">
          {!template.editable ? <Badge>staff only</Badge> : template.active ? <Badge tone="ok">custom · v{template.active.version}</Badge> : <Badge>built-in</Badge>}
          {template.draft ? <Badge tone="accent">draft</Badge> : null}
        </span>
      </td>
      <td className="px-5 py-3 text-right">
        <Button onClick={onOpen}>{selected ? 'Close' : template.editable ? 'Edit' : 'View'}</Button>
      </td>
    </tr>
  );
}

function EmailPreview({ email }: { email: RenderedEmailPreview }) {
  const [view, setView] = useState<'html' | 'text'>('html');
  return (
    <div className="space-y-3 px-5 py-4">
      <p className="text-sm text-ink-600">
        <span className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Subject</span> <span className="text-ink-900">{email.subject}</span>
      </p>
      <div className="flex gap-2">
        <Button variant={view === 'html' ? 'primary' : 'secondary'} onClick={() => setView('html')}>
          Formatted
        </Button>
        <Button variant={view === 'text' ? 'primary' : 'secondary'} onClick={() => setView('text')}>
          Plain text
        </Button>
      </div>
      {view === 'html' ? (
        // Sandboxed with nothing allowed: no scripts, no forms, no navigation out of the frame.
        <iframe title="Email preview" sandbox="" srcDoc={email.html} className="h-[720px] w-full rounded border border-line" />
      ) : (
        <pre className="max-h-[720px] overflow-y-auto rounded border border-line bg-page px-4 py-3 text-xs whitespace-pre-wrap text-ink-700">{email.text}</pre>
      )}
    </div>
  );
}

function VersionRow({ version }: { version: EmailWordingVersion }) {
  return (
    <tr className="border-b border-line last:border-0">
      <td className="tabular px-5 py-3 text-sm text-ink-900">v{version.version}</td>
      <td className="px-5 py-3">
        <Badge tone={statusTone(version.status)}>{version.status.toLowerCase()}</Badge>
      </td>
      <td className="px-5 py-3 text-xs whitespace-nowrap text-ink-400">{version.activated_at ? formatWhen(version.activated_at) : 'never'}</td>
      <td className="px-5 py-3 text-sm text-ink-600">{version.subject ?? <span className="text-ink-400">built-in</span>}</td>
      <td className="px-5 py-3 text-sm text-ink-600">{version.change_note ?? '—'}</td>
    </tr>
  );
}
